/**
 * Modal to reactivate a suspended user with a license selection.
 *
 * Extracted from UserManagementPage.tsx (Sub-PR 6.3).
 */

import { X, Loader2, Shield } from 'lucide-react'
import type { User } from '../../types'
import type { AvailableLicenseForSeat } from '../../services/admin'
import { LicenseOptionRadio } from './LicenseOptionRadio'

interface ReactivateModalProps {
  user: User
  reactivateLicenses: AvailableLicenseForSeat[]
  selectedLicense: string
  setSelectedLicense: (id: string) => void
  formError: string | null
  formLoading: boolean
  onClose: () => void
  onConfirm: () => void
}

export function ReactivateModal(props: ReactivateModalProps): React.ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-stone-800">
        <div className="flex items-center justify-between border-b border-stone-200 p-4 dark:border-stone-700">
          <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
            Reactivate User
          </h2>
          <button
            onClick={props.onClose}
            className="rounded p-1 hover:bg-stone-100 dark:hover:bg-stone-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <ReactivateBody {...props} />
      </div>
    </div>
  )
}

function ReactivateBody({
  user,
  reactivateLicenses,
  selectedLicense,
  setSelectedLicense,
  formError,
  formLoading,
  onClose,
  onConfirm,
}: ReactivateModalProps): React.ReactElement {
  return (
    <div className="space-y-4 p-4">
      <p className="text-sm text-stone-600 dark:text-stone-400">
        Select a licence to assign a seat to{' '}
        <span className="font-medium">{user.displayName ?? user.username}</span>. Multiple licences
        have available seats.
      </p>
      {formError && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
          {formError}
        </div>
      )}
      <div>
        <label className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
          Select Licence
        </label>
        <div className="space-y-2">
          {reactivateLicenses.map((license) => (
            <LicenseOptionRadio
              key={license.licenseId}
              option={{
                id: license.licenseId,
                licenseKey: license.licenseKey,
                seatsAvailable: license.seatsAvailable,
                seatsTotal: license.seatsTotal,
                validUntil: license.validUntil,
              }}
              groupName="reactivate-license"
              selectedId={selectedLicense}
              onSelect={setSelectedLicense}
            />
          ))}
        </div>
        {reactivateLicenses.length === 0 && (
          <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
            No available licences for this organisation. Free up seats or add a new licence.
          </p>
        )}
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg px-4 py-2 text-stone-700 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-700"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={formLoading || !selectedLicense}
          className="flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-white hover:bg-[#5c6e94] disabled:opacity-50"
        >
          {formLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Shield className="h-4 w-4" />
          )}
          Reactivate
        </button>
      </div>
    </div>
  )
}
